import React, { useState, useEffect, useMemo } from "react"
import axios from 'axios'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts'
import { convertTimestampToDate, convertSecondsToDuration } from './../utils'
import { weatherDescriptions, weekdays } from '../constants'

const secondsInDay = 86400
const secondsInWeek = secondsInDay * 7

const panelStyle = {
  position: "relative"
}

const panelTableStyle = {
  tableLayout: "fixed"
}

const cellStyle = {
  borderWidth: 0,
  borderRight: "1px solid #ccc",
  maxWidth: "100%",
  padding: "8px 24px"
}

function getTemperature(weather, time) {
  const temp = weather.temperature[time.toString() + '.0']
  if (temp) {
    return parseFloat(temp).toFixed(1) + ' °C'
  }
  else return
}

function getWeatherCondition(weather, time) {
  const cond = weather.condition[time.toString() + '.0']
  if (cond) {
    const row = weatherDescriptions[cond]
    return row[1];
  }
  else return
}

function getWeatherConditionIcon(weather, time) {
  const cond = weather.condition[time.toString() + '.0']
  if (cond) {
    const row = weatherDescriptions[cond]
    return (
      <img
        className="d-block m-auto"
        alt={row[1]}
        src={"https://openweathermap.org/img/wn/" + ('0' + row[2]).substr(-2) + "d@2x.png"}
      />
    )
  }
  else return null
}

function getDuration(duration, time) {
  let data = []
  for (let i = 6; i < 18; i++) {
    const dur = duration[(time + i * 3600).toString() + '.0']
    if (dur) {
      data.push(dur)
    }
  }
  return data
}

function getDurationMin(duration, time) {
  const data = getDuration(duration, time)
  if (data.length === 0) return ''
  return convertSecondsToDuration(Math.min.apply(Math, data) * 3600, false)
}

function getDurationMax(duration, time) {
  const data = getDuration(duration, time)
  if (data.length === 0) return ''
  return convertSecondsToDuration(Math.max.apply(Math, data) * 3600, false)
}

function ForecastChart({ facility, durations, weather, startTime, endTime }) {
  const duration = endTime - startTime
  const nWeeks = parseInt(duration / secondsInWeek)

  const data = useMemo(() => {
    let ndata = []
    if (!durations[facility.id]) return []
    for (const [key, value] of Object.entries(durations[facility.id])) {
      ndata.push({
        time: parseFloat(key),
        duration: 3600 * parseFloat(value),
      })
    }
    return ndata
  }, [facility.id, durations])

  let periods = []
  for (let i = 0; i < nWeeks; i++) {
    let week = {start: startTime + (i * secondsInWeek), end: startTime + ((i + 1) * secondsInWeek), days: []};
    for (let j = 0; j < 7; j++) {
      let time = startTime + (i * secondsInWeek) + (j * secondsInDay);
      week.days.push({
        time: time,
        weekday: weekdays[new Date(time * 1000).getDay()],
      })
    }
    periods.push(week)
  }

  return (
    <>
    {periods.map((period) => (
    <div style={panelStyle}>
      <ResponsiveContainer key={period.start} width="100%" height={300}>
        <AreaChart width={600} height={400} data={data} margin={{ top: 0, right: 10, left: -50, bottom: -30 }}>
          <defs>
            <linearGradient id="colorDuration" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#fdaa48" stopOpacity={0.6}/>
              <stop offset="95%" stopColor="#fdaa48" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis
           dataKey="time" scale="time" type="number"
           domain={[period.start, period.end]}
           allowDataOverflow={true}
           tick={false}
          />
          <YAxis tick={false} tickFormatter={tick => convertSecondsToDuration(tick, false)} domain={[5400, 10800]} />
          <CartesianGrid stroke="#f5f5f5" />
          <Area type="monotone" dataKey="duration" stroke="#fdaa4860" fillOpacity={1} fill="url(#colorDuration)" />
        </AreaChart>
      </ResponsiveContainer>
      <table className="table mask align-items-center table-flush" style={panelTableStyle}>
        <tr className="border-0">
        {period.days.map((day) => (
          <th key={day} scope="row" style={cellStyle}>
            <div className="row">
              <div className="col text-right pr-1 d-md-block d-none">{day.weekday}</div>
              <div className="col text-right pr-1 d-md-none d-block">{day.weekday.substring(0, 2)}</div>
              <div className="col text-right">{convertTimestampToDate(day.time, false)}</div>
            </div>
          </th>
        ))}
        </tr>
        <tr className="border-0">
        {period.days.map((day) => (
          <td key={day} style={cellStyle}>
            {durations[facility.id] && <div className="row">
              <div className="col text-center d-md-block d-none">Lead time:</div>
              <div className="col text-left d-md-block d-none">{getDurationMin(durations[facility.id], day.time)} - {getDurationMax(durations[facility.id], day.time)}</div>
              <div className="col text-left d-md-none d-block">
                <div className="row pl-2">{getDurationMin(durations[facility.id], day.time)}</div>
                <div className="row pl-2">{getDurationMax(durations[facility.id], day.time)}</div>
              </div>
            </div>}
          </td>
        ))}
        </tr>
        <tr className="border-0">
        {period.days.map((day) => (
        <td key={day} style={cellStyle}>
          {weather.condition &&
            <>
            <div className="row">{getWeatherConditionIcon(weather, day.time + 43200)}</div>
            <div className="row">
              <div className="col text-center d-md-block d-none">{getWeatherCondition(weather, day.time + 43200)}</div>
            </div>
            </>}
        </td>
        ))}
        </tr>
        <tr className="border-0">
        {period.days.map((day) => (
        <td key={day} className="text-center" style={cellStyle}>
          {weather.temperature && getTemperature(weather, day.time + 43200)}
        </td>
        ))}
        </tr>
      </table>
    </div>
    )
    )}
    </>
  )
}

export default function Forecast({ facility }) {
  const [durations, setDuration] = useState([])
  const [weather, setWeather] = useState([])
  const days = useMemo(() => 14, [])
  const [startTime, endTime] = useMemo(() => {
    const d = new Date()
    d.setHours(0)
    d.setMinutes(0)
    d.setSeconds(0)
    d.setMilliseconds(0)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    const start = new Date(d.setDate(diff))
    return [
      parseInt(start.getTime() / 1000),
      parseInt(new Date(d.setDate(start.getDate() + days)).getTime() / 1000),
    ]
  }, [days])

  useEffect(() => {
    const fetchPred = async () => {
      try {
        let rsp = await axios.get('/api/v1/predict/duration-svr', { params: {
          facilities: facility.id,
          start: startTime,
          end: endTime,
        }})
        setDuration(rsp.data?.result)
      } catch (e) {
        console.error(e)
      }
    }
    fetchPred()
  }, [facility.id, startTime, endTime])

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const params = { lon: facility.longitude, lat: facility.latitude, start: startTime, end: endTime }
        const rsp = await axios.get('/api/v1/weather/', { params })
        setWeather(rsp.data)
      } catch (e) {
        console.error(e)
      }
    }
    fetchWeather()
  }, [facility.longitude, facility.latitude, startTime, endTime])

  return (
    <div className='container-fluid'>
      <div className="row">
        <div className="col card">
          <div className="card-header bg-transparent">
            <div className="row align-items-center">
              <div className="col">
                <h5 className="card-title text-uppercase text-muted mb-0">{facility?.name}</h5>
                <span className="h2 font-weight-bold mb-0">Daily forecast</span>
              </div>
            </div>
          </div>
          <div className="card-body pt-0 pb-1">
            {durations && <ForecastChart
              facility={facility}
              durations={durations}
              weather={weather}
              startTime={startTime}
              endTime={endTime}
            />}
          </div>
        </div>
      </div>
    </div>
  )
}